import React, { useState, useEffect } from 'react';
import { MediaKit } from './MediaKit';
import './InfluencerProfileModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

// Meta/TikTok OAuth is paused; reach and content come from the July media kit.
const SOCIAL_OAUTH_ENABLED = false;

function InfluencerProfileModal({ influencerId, onClose, onInvite }) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/auth/influencer/${influencerId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await response.json();
        
        if (response.ok) {
          setProfile(data);
        } else {
          setError(data.message || 'Failed to load profile');
        }
      } catch (err) {
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [influencerId]);

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      ),
      tiktok: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      ),
      youtube: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
      facebook: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      twitter: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      snapchat: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.206 0c1.79.005 3.42.79 4.531 2.16.7.865 1.049 1.94 1.049 3.28 0 .3-.02.72-.05 1.16.15.08.36.13.57.13.31 0 .67-.09 1.01-.24.11-.05.23-.07.35-.07.42 0 .78.29.78.71 0 .53-.6.79-1.16.99-.35.13-.83.28-.94.55-.06.15-.03.34.09.57l.01.02c.03.06 1.02 2.24 3.16 2.59.28.05.48.29.46.57 0 .07-.02.15-.05.22-.23.53-1.18.92-2.91 1.19-.06.08-.11.35-.16.55-.05.21-.1.42-.18.64-.08.24-.26.36-.53.36h-.03c-.19 0-.44-.04-.75-.11-.44-.09-.94-.19-1.55-.19-.36 0-.73.03-1.11.1-.72.12-1.34.57-2.05 1.08-.94.68-2.01 1.45-3.63 1.45l-.14-.01-.13.01c-1.62 0-2.68-.77-3.62-1.45-.72-.51-1.33-.96-2.06-1.08-.38-.07-.75-.1-1.11-.1-.61 0-1.11.11-1.55.19-.31.06-.56.11-.75.11-.34 0-.5-.2-.56-.37-.08-.22-.13-.44-.18-.64-.05-.2-.1-.47-.16-.55-1.73-.27-2.68-.66-2.91-1.2-.03-.07-.05-.14-.05-.21-.02-.28.18-.52.46-.57 2.14-.35 3.13-2.53 3.16-2.59l.01-.02c.12-.23.15-.42.09-.57-.11-.27-.59-.42-.94-.55-.56-.2-1.16-.46-1.16-.99 0-.42.36-.71.78-.71.12 0 .24.02.35.07.34.15.7.24 1.01.24.21 0 .42-.05.57-.13-.03-.44-.05-.86-.05-1.16 0-1.34.35-2.42 1.05-3.28C8.372.79 10.003.005 11.793 0z"/>
        </svg>
      ),
    };
    return icons[platform] || null;
  };

  const getPlatformColor = (platform) => {
    const colors = {
      instagram: '#E4405F',
      tiktok: '#000000',
      youtube: '#FF0000',
      facebook: '#1877F2',
      twitter: '#000000',
      snapchat: '#FFFC00',
    };
    return colors[platform] || '#666';
  };

  if (isLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="influencer-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="loader"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="influencer-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">
            <p>{error}</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const { influencer, socialConnections, stats, mediaKit } = profile;
  const avatarUrl = influencer.profileImageUrl || mediaKit?.headshotUrl;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="influencer-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Header */}
        <div className="modal-header">
          <div className="profile-avatar-large">
            {avatarUrl ? (
              <img src={avatarUrl} alt={influencer.fullName} />
            ) : (
              <span>{influencer.fullName?.charAt(0) || 'C'}</span>
            )}
          </div>
          <div className="profile-header-info">
            <h2>{influencer.fullName}</h2>
            {influencer.location && (
              <p className="profile-location">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {influencer.location}
              </p>
            )}
            <div className="profile-stats-row">
              <div className="stat-item">
                <span className="stat-value">{stats.totalBids}</span>
                <span className="stat-label">Total Bids</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{stats.acceptedBids}</span>
                <span className="stat-label">Accepted</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{stats.acceptanceRate}%</span>
                <span className="stat-label">Success Rate</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bio */}
        {influencer.bio && (
          <div className="modal-section">
            <h3>About</h3>
            <p className="profile-bio">{influencer.bio}</p>
          </div>
        )}

        {/* Niches */}
        {influencer.niches && influencer.niches.length > 0 && (
          <div className="modal-section">
            <h3>Content Categories</h3>
            <div className="niches-tags">
              {influencer.niches.map((niche, idx) => (
                <span key={idx} className="niche-tag">{niche}</span>
              ))}
            </div>
          </div>
        )}

        {/* Media kit */}
        <div className="modal-section">
          <h3>Media kit</h3>
          <MediaKit
            mediaKit={mediaKit}
            emptyText="This creator's media kit hasn't been added yet. The Alooma team adds it when reviewing their account."
          />
        </div>

        {/* Connected Accounts */}
        {SOCIAL_OAUTH_ENABLED && (
        <div className="modal-section">
          <h3>Connected Accounts</h3>
          {socialConnections && socialConnections.length > 0 ? (
            <div className="social-accounts-grid">
              {socialConnections.map((conn, idx) => (
                <div 
                  key={idx} 
                  className="social-account-card"
                  style={{ '--platform-color': getPlatformColor(conn.platform) }}
                >
                  <div className="social-account-header">
                    <div className="platform-icon" style={{ color: getPlatformColor(conn.platform) }}>
                      {getPlatformIcon(conn.platform)}
                    </div>
                    <div className="account-info">
                      <span className="platform-name">{conn.platform}</span>
                      <span className="account-handle">@{conn.username}</span>
                      {conn.isVerified && <span className="verified-badge">✓</span>}
                    </div>
                  </div>
                  <div className="social-metrics-grid">
                    <div className="metric">
                      <span className="metric-value">{formatNumber(conn.followerCount)}</span>
                      <span className="metric-label">Followers</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{formatNumber(conn.mediaCount)}</span>
                      <span className="metric-label">Posts</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{conn.engagementRate?.toFixed(2) || '0.00'}%</span>
                      <span className="metric-label">Engagement</span>
                    </div>
                  </div>
                  {conn.avgLikes > 0 && (
                    <div className="social-extras">
                      <span>Avg. {formatNumber(conn.avgLikes)} likes</span>
                      <span>Avg. {formatNumber(conn.avgComments)} comments</span>
                    </div>
                  )}
                  <div className="last-synced">
                    Last synced: {new Date(conn.lastSyncedAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-accounts">
              <p>No verified social accounts linked yet.</p>
            </div>
          )}
        </div>
        )}

        {/* Rates */}
        {influencer.rates && influencer.rates.length > 0 && (
          <div className="modal-section">
            <h3>Typical Rates</h3>
            <div className="rates-grid">
              {influencer.rates.map((rate, idx) => (
                <div key={idx} className="rate-item">
                  <span className="rate-type">{rate.type?.replace(/_/g, ' ')}</span>
                  <span className="rate-amount">${parseInt(rate.amount).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Partnerships */}
        {influencer.pastPartnerships && (
          <div className="modal-section">
            <h3>Past Partnerships</h3>
            <p className="partnerships-text">{influencer.pastPartnerships}</p>
          </div>
        )}

        {/* Member Since */}
        <div className="modal-footer">
          <span className="member-since">
            Member since {new Date(influencer.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          {onInvite && (
            <button
              className="modal-invite-btn"
              onClick={() => onInvite(influencerId, influencer.fullName)}
            >
              Invite to Bid
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InfluencerProfileModal;
